import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  AccountCircleOutlined,
  BadgeOutlined,
  EmailOutlined,
  GroupsOutlined,
  SearchOutlined,
  WorkOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";

import { useSearchParams } from "react-router-dom";
import { fetchADUser } from "utils/adUserFetch";
import "./ActiveDirectoryUserLookup.css";

// ── UI sub-components ─────────────────────────────────────────────────────────

const FieldRow = ({ icon, label, value = "" }) => (
  <div className="ecr-user-lookup__field">
    <span className="ecr-user-lookup__field-icon">{icon}</span>
    <div>
      <p className="ecr-user-lookup__field-label">{label}</p>
      <p className="ecr-user-lookup__field-value">
        {value || <span className="ecr-user-lookup__field-empty">—</span>}
      </p>
    </div>
  </div>
);

FieldRow.propTypes = {
  icon: PropTypes.element.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};



const UserAvatar = ({ name = "" }) => {
  const initials = name
    ? name
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase()
    : "?";
  return <div className="ecr-user-lookup__avatar">{initials}</div>;
};

UserAvatar.propTypes = {
  name: PropTypes.string,
};



// ── Main component ────────────────────────────────────────────────────────────

const ActiveDirectoryUserLookup = () => {
  const [searchParams] = useSearchParams();
  const urlEmail = searchParams.get("email") || "";

  const [email, setEmail] = useState(urlEmail);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [notFound, setNotFound] = useState(false);

  const doSearch = useCallback(async (emailValue) => {
    const trimmed = emailValue?.trim();
    if (!trimmed) return;
    setIsLoading(true);
    setErrorMsg("");
    setNotFound(false);
    setUser(null);
    try {
      const result = await fetchADUser(trimmed);
      if (result) {
        setUser(result);
      } else {
        setNotFound(true);
      }
    } catch (err) {
      setErrorMsg(
        err?.message ||
        "Failed to retrieve user from Active Directory. Please check the email address and try again."
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleSearch = useCallback(() => {
    doSearch(email);
  }, [email, doSearch]);

  // Auto-trigger when the shared search bar updates the URL param
  const lastTriggeredEmail = useRef("");
  useEffect(() => {
    if (urlEmail && urlEmail !== lastTriggeredEmail.current) {
      lastTriggeredEmail.current = urlEmail;
      setEmail(urlEmail);
      doSearch(urlEmail);
    }
  }, [urlEmail, doSearch]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <div className="ecr-user-lookup">
        {/* Search bar */}
        <div className="ecr-user-lookup__search-wrap">
          <TextField
            fullWidth
            size="small"
            placeholder="Enter user email address (e.g. jsmith@deloitte.com)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ fontSize: 18, color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "14px",
                background: "#fff",
                borderRadius: "8px 0 0 8px",
                "& fieldset": { borderRight: "none" },
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={isLoading || !email.trim()}
            sx={{
              background: "#86BC25",
              color: "#fff",
              borderRadius: "0 8px 8px 0",
              fontWeight: 700,
              fontSize: "13px",
              px: 3,
              height: "40px",
              flexShrink: 0,
              "&:hover": { background: "#6fa019" },
              "&.Mui-disabled": { background: "#d1d5db", color: "#9ca3af" },
            }}
          >
            {isLoading ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : (
              "Search"
            )}
          </Button>
        </div>

        {/* Error state */}
        {errorMsg && (
          <Alert severity="error" sx={{ fontSize: "13px", borderRadius: "8px" }}>
            {errorMsg}
          </Alert>
        )}

        {/* Not found */}
        {notFound && (
          <Alert severity="warning" sx={{ fontSize: "13px", borderRadius: "8px" }}>
            No Active Directory user found for <strong>{email.trim()}</strong>.
          </Alert>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="ecr-user-lookup__skeleton-wrap">
            <CircularProgress sx={{ color: "#86BC25" }} />
            <p style={{ color: "#9ca3af", fontSize: "13px", marginTop: 12 }}>
              Looking up user in Active Directory…
            </p>
          </div>
        )}

        {/* User card */}
        {!isLoading && user && (
          <div className="ecr-user-lookup__card">
            {/* Header */}
            <div className="ecr-user-lookup__card-header">
              <UserAvatar name={user.displayName} />
              <div>
                <h2 className="ecr-user-lookup__card-name">
                  {user.displayName || "—"}
                </h2>
                <p className="ecr-user-lookup__card-title">
                  {user.jobTitle || "No job title"}
                </p>
                <span className="ecr-user-lookup__card-badge">Active Directory</span>
              </div>
            </div>

            <Divider sx={{ my: 2, borderColor: "#f3f4f6" }} />

            {/* Fields */}
            <div className="ecr-user-lookup__fields">
              <FieldRow
                icon={<EmailOutlined />}
                label="Email"
                value={user.mail}
              />
              <FieldRow
                icon={<AccountCircleOutlined />}
                label="User Principal Name"
                value={user.userPrincipalName}
              />
              <FieldRow
                icon={<WorkOutlined />}
                label="Department"
                value={user.department}
              />
              <FieldRow
                icon={<WorkOutlined />}
                label="Company"
                value={user.companyName}
              />
              <FieldRow
                icon={<BadgeOutlined />}
                label="Country"
                value={user.country}
              />
              <FieldRow
                icon={<BadgeOutlined />}
                label="City"
                value={user.city}
              />
              <FieldRow
                icon={<AccountCircleOutlined />}
                label="Domain"
                value={user.onPremisesDomainName}
              />
              <FieldRow
                icon={<WorkOutlined />}
                label="Office"
                value={user.physicalDeliveryOfficeName || user.officeLocation}
              />
            </div>

            {/* Groups */}
            {Array.isArray(user.groups) && user.groups.length > 0 && (
              <>
                <Divider sx={{ my: 2, borderColor: "#f3f4f6" }} />
                <div className="ecr-user-lookup__groups">
                  <div className="ecr-user-lookup__groups-label">
                    <GroupsOutlined sx={{ fontSize: 16 }} />
                    <span>Group Memberships ({user.groups.length})</span>
                  </div>
                  <div className="ecr-user-lookup__groups-list">
                    {user.groups.map((g, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Tooltip key={i} title={g} arrow>
                        <span className="ecr-user-lookup__group-chip">{g}</span>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Raw response */}
            <details className="ecr-user-lookup__raw">
              <summary>Raw AD response</summary>
              <pre>{JSON.stringify(user, null, 2)}</pre>
            </details>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActiveDirectoryUserLookup;
